import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import StarIconPointy from '@pkg/assets/icons/v2/star-outline.svg?react';
import { useToastStore } from '@pkg/components';
import { useRemoveWishlistItemsMutation } from '@pkg/hooks/queryHooks/useWishlists.ts';
import { useAddWishlistItems } from '@pkg/hooks/wishlists/useWishlistItems.ts';
import { type SupportedChain } from '@pkg/types';

import { Count, Icon, Wrapper } from './styles';

interface WishlistButtonProps {
    contract?: string;
    token_id?: string;
    chain?: SupportedChain;
    wishlistSlug?: string;
    count: number;
}

export const WishlistButton = ({ contract, token_id, chain, wishlistSlug, count }: WishlistButtonProps) => {
    const [isAddedItem, setIsAdded] = useState(!!wishlistSlug);
    const { removeCollectible, removeCollection } = useRemoveWishlistItemsMutation();
    useEffect(() => {
        setIsAdded(!!wishlistSlug);
    }, [wishlistSlug]);
    const tokenIdFormatted = `${chain}.${contract}.${token_id}`;
    const collectionIdFormatted = `${chain}.${contract}`;
    const { t: translate } = useTranslation(['nft_details']);
    const { addToast } = useToastStore();
    const { addWishlistItem } = useAddWishlistItems();

    const isLoading = removeCollectible.isPending || removeCollection.isPending;
    const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (isLoading) return;

        if (wishlistSlug) {
            setIsAdded(false);
            if (token_id) {
                removeCollectible
                    .mutateAsync({
                        slug: wishlistSlug,
                        collectiblesIds: [tokenIdFormatted],
                    })
                    .then((r) => {
                        if (!r?.errors) {
                            addToast({
                                emoji: '💥',
                                message: `Collectible has been removed a from ${wishlistSlug}`,
                                messageAction: {
                                    onAction: () =>
                                        addWishlistItem({ slug: wishlistSlug, collectiblesIds: [tokenIdFormatted] }),
                                    copy: 'Undo',
                                },
                            });
                        }
                    });
            } else {
                removeCollection
                    .mutateAsync({
                        slug: wishlistSlug,
                        collectionsIds: [collectionIdFormatted],
                    })
                    .then((r) => {
                        if (!r.errors) {
                            addToast({
                                emoji: '💥',
                                message: `Collection has been removed a from ${wishlistSlug}`,
                                messageAction: {
                                    onAction: () =>
                                        addWishlistItem({
                                            slug: wishlistSlug,
                                            collectionsIds: [collectionIdFormatted],
                                        }),
                                    copy: 'Undo',
                                },
                            });
                        }
                    });
            }
        } else {
            setIsAdded(true);
            if (token_id) {
                await addWishlistItem({
                    collectiblesIds: [tokenIdFormatted],
                });
            } else {
                await addWishlistItem({
                    collectionsIds: [collectionIdFormatted],
                });
            }
        }
    };

    return (
        <Wrapper
            $isLoading={isLoading}
            $isAdded={isAddedItem}
            onClick={handleClick}
            title={
                isAddedItem ? translate('detail_view_remove_from_wishlist') : translate('detail_view_add_to_wishlist')
            }
        >
            <Icon>
                <StarIconPointy />
            </Icon>
            {count ? <Count>{count}</Count> : null}
        </Wrapper>
    );
};
